import { useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/site/motion";
import { Eyebrow } from "@/components/site/ui";
import { ZONES_GDP, SECTOR_SPLIT, CHART_COLORS } from "@/data/content";

const BarTip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-navy/10 bg-white px-3 py-2 shadow-lg">
      <p className="font-display text-sm text-navy">{payload[0].payload.flag} {label}</p>
      <p className="text-sm text-gold font-semibold">{payload[0].value}% de croissance PIB</p>
    </div>
  ) : null;

const PieTip = ({ active, payload }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-navy/10 bg-white px-3 py-2 shadow-lg">
      <p className="font-display text-sm text-navy">{payload[0].name}</p>
      <p className="text-sm text-gold font-semibold">{payload[0].value}% du PIB</p>
    </div>
  ) : null;

export const StrategyDashboard = () => {
  const [activeIdx, setActiveIdx] = useState(-1);
  const [exporting, setExporting] = useState(false);
  const chartsRef = useRef(null);

  const exportReport = async () => {
    if (!chartsRef.current) return;
    setExporting(true);
    try {
      const [{ default: jsPDF }, html2canvas] = await Promise.all([
        import("jspdf"),
        import("html2canvas").then((m) => m.default),
      ]);
      const canvas = await html2canvas(chartsRef.current, { scale: 2, backgroundColor: "#F0EBDD", useCORS: true });
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const margin = 14;

      // Header band
      pdf.setFillColor(0, 12, 61);
      pdf.rect(0, 0, pageW, 30, "F");
      pdf.setTextColor(232, 163, 61);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.text("bridge partners", margin, 15);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.text("Tableau de bord stratégique — France & Outre-mer · Bénin · Côte d'Ivoire", margin, 23);

      // Summary text
      pdf.setTextColor(74, 74, 74);
      pdf.setFontSize(11);
      const today = new Date().toLocaleDateString("fr-FR");
      pdf.text(`Rapport généré le ${today}`, margin, 42);
      const summary =
        "Synthèse stratégique : Bridge Partners opère sur trois zones complémentaires. La France (+1,1%) " +
        "et les Outre-mer (+2,5%) constituent notre base européenne, plateforme d'accès à l'écosystème " +
        "financier et à la diaspora. En Afrique de l'Ouest, le Bénin (+6,4%) et la Côte d'Ivoire (+7,0%) " +
        "affichent une croissance parmi les plus dynamiques du continent (moyenne régionale : 5,8%). " +
        "L'économie africaine reste dominée par les services (48%), suivis de l'industrie (29%) et de " +
        "l'agriculture (23%), signe d'une diversification en marche. Ce triple ancrage — Europe + Afrique — " +
        "positionne Bridge Partners au cœur des flux d'investissement Nord-Sud.";
      const lines = pdf.splitTextToSize(summary, pageW - margin * 2);
      pdf.text(lines, margin, 52);

      // Charts image
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, 52 + lines.length * 6 + 6, imgW, imgH);

      // Footer
      pdf.setFontSize(9);
      pdf.setTextColor(150, 150, 150);
      pdf.text("Bridge Partners · Paris · Cotonou · Abidjan · contact@bridgepartners.fr", margin, 288);

      pdf.save("bridge-partners-tableau-de-bord.pdf");
      toast.success("Rapport PDF généré.");
    } catch (e) {
      toast.error("Impossible de générer le rapport.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="bg-cream-dark py-24 sm:py-32" data-testid="strategy-dashboard">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Eyebrow>Intelligence économique</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium tracking-tight text-navy">
              Tableau de bord <em className="not-italic italic text-gold-gradient">stratégique</em>
            </h2>
            <p className="mt-4 text-anthracite font-body leading-relaxed">
              Une lecture data-driven de nos trois zones d'ancrage — <strong className="text-navy">France & Outre-mer</strong>, <strong className="text-navy">Bénin</strong> et <strong className="text-navy">Côte d'Ivoire</strong> — mise en perspective avec l'Afrique de l'Ouest.
            </p>
          </div>
          <button
            onClick={exportReport}
            disabled={exporting}
            data-testid="export-report-button"
            className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-gradient-to-r hover:from-gold hover:to-gold-light hover:text-navy disabled:opacity-60"
          >
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {exporting ? "Génération…" : "Exporter le rapport"}
          </button>
        </Reveal>

        <div ref={chartsRef} className="mt-14 grid gap-6 lg:grid-cols-5 bg-cream-dark p-1 rounded-3xl">
          {/* Bar chart */}
          <Reveal className="lg:col-span-3">
            <div className="rounded-3xl border border-navy/10 bg-white p-7 sm:p-9 h-full" data-testid="chart-gdp">
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-xl text-navy">Croissance du PIB par zone d'ancrage</h3>
                <span className="text-xs uppercase tracking-wide text-anthracite/50">est. 2025 · %</span>
              </div>
              <div className="mt-8 h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ZONES_GDP} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <XAxis dataKey="zone" tickLine={false} axisLine={false} tick={{ fill: "#4A4A4A", fontSize: 12 }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fill: "#4A4A4A", fontSize: 12 }} />
                    <Tooltip cursor={{ fill: "rgba(0,22,95,0.04)" }} content={<BarTip />} />
                    <Bar dataKey="pib" radius={[8, 8, 0, 0]} onMouseEnter={(_, i) => setActiveIdx(i)} onMouseLeave={() => setActiveIdx(-1)}>
                      {ZONES_GDP.map((e, i) => (
                        <Cell key={i} fill={i === activeIdx ? "#D4A017" : (e.zone === "France" || e.zone === "Outre-mer") ? "#4A5A8F" : "#00165F"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-anthracite/60 font-body">
                Base européenne stable (France & Outre-mer), marchés africains à forte croissance (Bénin, Côte d'Ivoire).
              </p>
            </div>
          </Reveal>

          {/* Donut chart */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="rounded-3xl border border-navy/10 bg-white p-7 sm:p-9 h-full" data-testid="chart-sectors">
              <h3 className="font-display text-xl text-navy">Répartition sectorielle</h3>
              <span className="text-xs uppercase tracking-wide text-anthracite/50">moyenne continent · %</span>
              <div className="mt-6 h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={SECTOR_SPLIT} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={100} paddingAngle={3} stroke="none">
                      {SECTOR_SPLIT.map((e, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTip />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 13, color: "#4A4A4A" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
